import { Link } from "react-router-dom";
import { Linkedin, Instagram, MapPin, Mail } from "lucide-react";
import logoUrl from "@/assets/fusion-logo-new.png";
import { useLanguage } from "@/contexts/LanguageContext";

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const SOCIAL_LINKS = [
  { Icon: Linkedin, href: "https://www.linkedin.com/company/fusion-media-sa", label: "LinkedIn" },
  { Icon: XIcon, href: "https://x.com/FusionSA", label: "X" },
  { Icon: Instagram, href: "https://www.instagram.com/fusionsa_/", label: "Instagram" },
];

export const Footer = () => {
  const { language } = useLanguage();
  const ar = language === "ar";

  return (
    <footer className="mt-20 bg-gradient-to-br from-[hsl(220_60%_10%)] via-[hsl(220_55%_8%)] to-[hsl(220_60%_6%)] text-white">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 py-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <img src={logoUrl} alt="Fusion Media" className="h-[11.75rem] w-auto object-contain mt-[-4.5rem] mb-[-5.5rem] -ml-6 brightness-0 invert" />
          <p className="text-sm text-white/70 max-w-xs leading-relaxed mt-6">
            {ar
              ? "منصة إعلانية مدعومة بالذكاء الاصطناعي للعصر الجديد من التواصل الذكي."
              : "The all-in-one AI-powered advertising platform for the new era of smart communication."}
          </p>
          <div className="flex gap-2 mt-5">
            {SOCIAL_LINKS.map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label={label}
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-sm font-semibold mb-4">{ar ? "الحلول" : "Solutions"}</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/partners" className="hover:text-white">Programmatic DOOH</Link></li>
            <li><Link to="/campaign" className="hover:text-white">AI Campaign Creator</Link></li>
            <li><Link to="/influencers" className="hover:text-white">Influencer Advertising</Link></li>
            <li><Link to="/events-sponsors" className="hover:text-white">Event Sponsorships</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-sm font-semibold mb-4">{ar ? "الشركة" : "Company"}</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/who-we-are" className="hover:text-white">{ar ? "من نحن" : "About Us"}</Link></li>
            <li><Link to="/partners" className="hover:text-white">{ar ? "شركاؤنا" : "Our Partners"}</Link></li>
            <li><Link to="/affiliate-program" className="hover:text-white">{ar ? "شريك إحالة" : "Affiliates"}</Link></li>
            <li><Link to="/contact" className="hover:text-white">{ar ? "اتصل بنا" : "Contact"}</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-sm font-semibold mb-4">{ar ? "الدعم" : "Support"}</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/faqs" className="hover:text-white">FAQs</Link></li>
            <li><Link to="/contact" className="hover:text-white">Help Center</Link></li>
            <li><Link to="/privacy-policy" className="hover:text-white">{ar ? "الخصوصية" : "Privacy"}</Link></li>
            <li><Link to="/return-policy" className="hover:text-white">{ar ? "الاسترداد" : "Refunds"}</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-sm font-semibold mb-4">{ar ? "تواصل" : "Contact"}</h4>
          <ul className="space-y-3 text-sm text-white/70">
            <li className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> support@fusion-media.sa</li>
            <li className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> Riyadh, Saudi Arabia</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 py-5 text-xs text-white/50 flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} Fusion Media. {ar ? "جميع الحقوق محفوظة." : "All rights reserved."}</span>
          <span>www.fusion-media.sa</span>
        </div>
      </div>
    </footer>
  );
};
